import { forwardRef, ReactNode } from "react";
import { MOBILE_MEDIA_DEVICE_ID } from "@yujacorp/verity-api-contract";
import i18nPackage from "../../i18n/i18n";
import { BasicStep } from "../components/basic-step";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepHeader,
	BasicSubstepTitle,
	combineSubstepStatuses,
	getBasicSubstepIcon,
	SubstepStatus,
} from "../components/basic-substep";
import styles from "./index.module.css";
import {
	CameraPreview,
	CameraPreviewProps,
	Waveform,
	AudioErrorType,
	WaveformProps,
	DeviceDropdown,
	DeviceDropdownProps,
	DeviceErrorType,
} from "../components/media-components";
import { Box, Typography } from "@mui/material";
import { CoreButton, CoreSpinner } from "../../core";
import { DeviceSetupKey } from "../before-quiz";
import Guide from "../components/guide";
import deviceSelected from "../../images/phone-success.svg";

type CameraOptions = {
	status: SubstepStatus;
	devices: Omit<MediaDeviceInfo, "toJSON">[];
	cameraPreview?: CameraPreviewProps;
	retryStreamCreation?: () => Promise<void>;
} & Pick<DeviceDropdownProps, "selectedDeviceId" | "onChange">;

type CameraSubstepProps = Omit<BaseSubstepProps, "status"> & {
	container?: DeviceDropdownProps["container"];
	primary: CameraOptions;
	secondary?: CameraOptions;
	requestPermission?: {
		initiate: () => void;
		disabled?: boolean;
	};
};

function MobileSelectedMessage() {
	return (
		<div className={styles.mobileSelectedMessage}>
			<img className={styles.mobileSelectedImage} src={deviceSelected} alt="" />
			<div className={styles.mobileSelectedText}>
				<div className={styles.mobileSelectedTitle}>
					{i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.mobileSelected.title")}
				</div>
				{i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.mobileSelected.description")}
			</div>
		</div>
	);
}

function CameraSelector({
	allowMobile = false,
	status,
	devices,
	cameraPreview,
	selectedDeviceId,
	onChange,
	container,
	label,
}: CameraOptions &
	Pick<DeviceDropdownProps, "label" | "container"> & {
		allowMobile?: boolean;
	}) {
	const mobileDeviceSelected = allowMobile && selectedDeviceId === MOBILE_MEDIA_DEVICE_ID;

	return (
		<div className={styles.cameraSelector}>
			<DeviceDropdown
				type="camera"
				allowMobile={allowMobile}
				devices={devices}
				onChange={onChange}
				status={status}
				selectedDeviceId={selectedDeviceId}
				disabled={cameraPreview?.isVideoLoading && !cameraPreview.cameraError}
				container={container}
				label={label}
			/>
			{!mobileDeviceSelected && cameraPreview && (
				<div className={`${styles.videoContainer} ${styles.small}`}>
					<CameraPreview
						{...cameraPreview}
						canvasContainerClassName={`${styles.videoPreviewContainer} ${cameraPreview.canvasContainerClassName || ""}`}
					/>
				</div>
			)}
			{mobileDeviceSelected && <MobileSelectedMessage />}
		</div>
	);
}

function getStepText(
	combinedStatus: SubstepStatus,
	secondaryVideoRecording: boolean,
	mobileDeviceSelected: boolean,
	primaryError?: DeviceErrorType,
	secondaryError?: DeviceErrorType,
) {
	let title: string;
	let description: string;
	const sourceCountKey: "single" | "multiple" = secondaryVideoRecording ? "multiple" : "single";

	if (combinedStatus === "started" || combinedStatus === "unstarted") {
		title = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.unstarted.${sourceCountKey}`);
		description = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.description.unstarted.${sourceCountKey}`);
	} else if (combinedStatus === "failed") {
		if (secondaryVideoRecording) {
			if (primaryError && secondaryError && secondaryError !== primaryError) {
				title = i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.title.multipleErrors");
				description = i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.description.multipleErrors");
			} else {
				let errorKey: DeviceErrorType;
				let errorSourceKey: "primary" | "secondary" | "all";
				if (!!primaryError === !!secondaryError) {
					errorSourceKey = "all";
					errorKey = primaryError ?? "unknown";
				} else if (secondaryError) {
					errorSourceKey = "secondary";
					errorKey = secondaryError;
				} else {
					errorSourceKey = "primary";
					errorKey = primaryError ?? "unknown";
				}
				const alternateErrorSourceKey: "any" | "all" = errorSourceKey === "all" ? "all" : "any";

				if (errorKey === "notFound" || errorKey === "deviceInUse" || errorKey === "unknown") {
					title = i18nPackage.t(
						`beforeQuiz.deviceSetup.videoRecordingMode.title.${errorKey}.multiple.${errorSourceKey}`,
					);
				} else {
					title = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${errorKey}.multiple`);
				}

				if (errorKey === "noPermission" || errorKey === "deviceInUse" || errorKey === "notFound") {
					description = i18nPackage.t(
						`beforeQuiz.deviceSetup.videoRecordingMode.description.${errorKey}.multiple.${errorSourceKey}`,
					);
				} else {
					description = i18nPackage.t(
						`beforeQuiz.deviceSetup.videoRecordingMode.description.${errorKey}.multiple.${alternateErrorSourceKey}`,
					);
				}
			}
		} else {
			const errorKey = primaryError ?? "unknown";
			title = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${errorKey}.single`);
			description = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.description.${errorKey}.single`);
		}
	} else {
		if (sourceCountKey === "multiple") {
			const recordingMethodKey = mobileDeviceSelected ? "mobile" : "physical";
			title = i18nPackage.t(
				`beforeQuiz.deviceSetup.videoRecordingMode.title.${combinedStatus}.${sourceCountKey}.${recordingMethodKey}`,
			);
			description = i18nPackage.t(
				`beforeQuiz.deviceSetup.videoRecordingMode.description.${combinedStatus}.${sourceCountKey}.${recordingMethodKey}`,
			);
		} else {
			title = i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${combinedStatus}.${sourceCountKey}`);
			description = i18nPackage.t(
				`beforeQuiz.deviceSetup.videoRecordingMode.description.${combinedStatus}.${sourceCountKey}`,
			);
		}
	}
	return [title, description];
}

export function CameraSubstep({ primary, secondary, requestPermission, container, rootRef }: CameraSubstepProps) {
	const stepStatus = secondary ? combineSubstepStatuses(primary.status, secondary.status) : primary.status;
	const [title, description] = getStepText(
		stepStatus,
		!!secondary,
		secondary?.selectedDeviceId === MOBILE_MEDIA_DEVICE_ID,
		primary.cameraPreview?.cameraError,
		secondary?.cameraPreview?.cameraError,
	);

	let actionButton: ReactNode | undefined;
	if (requestPermission) {
		actionButton = (
			<CoreButton
				className={styles.mediaButton}
				onClick={requestPermission.initiate}
				disabled={requestPermission.disabled}
			>
				{i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.selectWebcam")}
			</CoreButton>
		);
	} else if (primary.retryStreamCreation || secondary?.retryStreamCreation) {
		actionButton = (
			<CoreButton
				className={styles.mediaButton}
				onClick={async () => {
					await primary.retryStreamCreation?.();
					await secondary?.retryStreamCreation?.();
				}}
			>
				{i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.tryAgain")}
			</CoreButton>
		);
	}
	const showActionButton = actionButton && (stepStatus === "unstarted" || stepStatus === "failed");

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.mediaContainer}>
				<div className={styles.mediaTopRow}>
					{getBasicSubstepIcon(stepStatus)}
					<div className={styles.mediaLeftRegion}>
						<div className={styles.mediaHeaderWrapper}>
							<BasicSubstepTitle title={title} />
							<BasicSubstepDescription description={description} />
						</div>
						{secondary ? (
							<div className={styles.videoSelectors} style={{ display: stepStatus === "unstarted" ? "none" : "" }}>
								<CameraSelector
									devices={primary.devices}
									status={primary.status}
									selectedDeviceId={primary.selectedDeviceId}
									onChange={primary.onChange}
									cameraPreview={primary.cameraPreview}
									label={i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.primaryCamera")}
									container={container}
								/>
								<CameraSelector
									allowMobile
									status={secondary.status}
									devices={secondary.devices}
									selectedDeviceId={secondary.selectedDeviceId}
									onChange={secondary.onChange}
									cameraPreview={secondary.cameraPreview}
									label={i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.secondaryCamera")}
									container={container}
								/>
							</div>
						) : (
							stepStatus !== "unstarted" && (
								<DeviceDropdown
									type="camera"
									devices={primary.devices}
									status={primary.status}
									selectedDeviceId={primary.selectedDeviceId}
									onChange={primary.onChange}
									disabled={primary.cameraPreview?.isVideoLoading && !primary.cameraPreview.cameraError}
									container={container}
								/>
							)
						)}
					</div>
				</div>
				{showActionButton && (
					<div className={styles.mediaButtonWrapper}>
						<Box className={styles.mediaButtonWrapper}>{actionButton}</Box>
					</div>
				)}
				{primary.cameraPreview && !secondary && (
					<>
						<div
							className={styles.videoContainer}
							style={{
								display: showActionButton || stepStatus === "unstarted" ? "none" : "",
							}}
						>
							<CameraPreview {...primary.cameraPreview} />
						</div>
					</>
				)}
			</div>
			{stepStatus === "failed" &&
				(primary.status === "failed" || secondary?.status !== "failed" ? (
					<VideoAudioGuide type="videoRecordingMode" />
				) : (
					<SecondaryVideoGuide />
				))}
		</BasicSubstep>
	);
}

type AudioSubstepProps = BaseSubstepProps & {
	audioError?: AudioErrorType;
	slotProps: {
		waveform: WaveformProps;
		dropdown: Omit<DeviceDropdownProps, "devices" | "type"> & {
			devices: Omit<MediaDeviceInfo, "toJSON">[] | undefined;
		};
	};
	requestPermission?: {
		initiate: () => void;
		disabled?: boolean;
	};
	retryStreamCreation?: () => Promise<void>;
};

export function AudioSubstep({
	status,
	audioError,
	slotProps,
	rootRef,
	requestPermission,
	retryStreamCreation,
}: AudioSubstepProps) {
	const { devices, onChange, selectedDeviceId, container } = slotProps.dropdown;
	const { volume, height } = slotProps.waveform;
	const statusKey =
		status === "started"
			? "unstarted"
			: status === "failed" && audioError !== "noAudio"
				? (audioError ?? "unknown")
				: status;
	let actionButton: ReactNode | undefined;
	if (requestPermission) {
		actionButton = (
			<CoreButton
				className={styles.mediaButton}
				onClick={requestPermission.initiate}
				disabled={requestPermission.disabled}
			>
				{i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.selectMicrophone")}
			</CoreButton>
		);
	} else if (retryStreamCreation) {
		actionButton = (
			<CoreButton className={styles.mediaButton} onClick={retryStreamCreation}>
				{i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.tryAgain")}
			</CoreButton>
		);
	}
	const showSelectAudioButton = actionButton && (status === "unstarted" || status === "failed");
	const showMicrophoneWaveform = status === "completed" || status === "started";
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.mediaContainer}>
				<div className={styles.mediaTopRow}>
					{getBasicSubstepIcon(status)}
					<div className={styles.mediaLeftRegion}>
						<div className={styles.mediaHeaderWrapper}>
							<BasicSubstepTitle
								title={i18nPackage.t(`beforeQuiz.deviceSetup.audioRecordingMode.title.${statusKey}`)}
							/>
							<BasicSubstepDescription
								description={i18nPackage.t(`beforeQuiz.deviceSetup.audioRecordingMode.description.${statusKey}`)}
							/>
						</div>
						{devices && status !== "unstarted" && (
							<DeviceDropdown
								devices={devices}
								onChange={onChange}
								status={status}
								selectedDeviceId={selectedDeviceId}
								container={container}
								type="audio"
							/>
						)}
					</div>
				</div>
				{showSelectAudioButton && <Box className={styles.mediaButtonWrapper}>{actionButton}</Box>}
				{showMicrophoneWaveform && (
					<div className={styles.audioWaveContainer}>
						{audioError === "noAudio" && (
							<div className={styles.noAudioWarning}>
								<Typography>
									{i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.description.noAudio")}
								</Typography>
							</div>
						)}
						{status === "completed" && <Waveform volume={volume} height={height} />}
						{status === "started" && (
							<Box width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"} height={"100%"}>
								<CoreSpinner size={40} />
							</Box>
						)}
					</div>
				)}
			</div>
			{status === "failed" && <VideoAudioGuide type="audioRecordingMode" />}
		</BasicSubstep>
	);
}

function VideoAudioGuide({ type }: { type: Extract<DeviceSetupKey, "audioRecordingMode" | "videoRecordingMode"> }) {
	return (
		<Guide
			description={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.guideDescription`)}
			iframeTitle={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.videoTitle`)}
			src={"https://demo.video.yuja.com/V/Video?v=14648831&node=63114343&a=103235564"}
			title={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.guideTitle`)}
			className={styles.videoAudioGuide}
		/>
	);
}

function SecondaryVideoGuide() {
	return (
		<Guide
			description={i18nPackage.t(`beforeQuiz.deviceSetup.secondaryVideoRecordingMode.guideDescription`)}
			iframeTitle={i18nPackage.t(`beforeQuiz.deviceSetup.secondaryVideoRecordingMode.videoTitle`)}
			src="https://marketing.video.yuja.com/V/Video?v=15594946&node=67401122&a=149203474"
			title={i18nPackage.t(`beforeQuiz.deviceSetup.secondaryVideoRecordingMode.guideTitle`)}
			className={styles.videoAudioGuide}
		/>
	);
}

type ScreenShareSubstepProps = BaseSubstepProps & {
	isMac: boolean;
	onShareScreen: () => void;
};

export function ScreenShareSubstep({ status, isMac, onShareScreen, rootRef }: ScreenShareSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.screenShareSubstep}>
				<BasicSubstepHeader
					title={i18nPackage.t(`beforeQuiz.deviceSetup.recordScreen.title.${statusKey}`)}
					description={
						status === "failed" && isMac
							? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.description.failedMac")
							: i18nPackage.t(`beforeQuiz.deviceSetup.recordScreen.description.${statusKey}`)
					}
					status={status}
				/>
				{(status === "failed" || status === "unstarted") && (
					<CoreButton className={styles.mediaButton} onClick={onShareScreen}>
						{status === "failed"
							? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.tryAgain")
							: i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.selectScreen")}
					</CoreButton>
				)}
			</div>
			{status === "failed" && <ScreenshareGuide isMac={isMac} />}
		</BasicSubstep>
	);
}

function ScreenshareGuide({ isMac }: { isMac: boolean }) {
	return (
		<Guide
			description={
				isMac
					? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.macGuide")
					: i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.windowGuide")
			}
			iframeTitle={i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.videoTitle")}
			src={
				isMac
					? "https://demo.video.yuja.com/V/Video?v=14648940&node=63114500&a=160471175"
					: "https://demo.video.yuja.com/V/Video?v=14648812&node=63114312&a=139696381"
			}
			title={i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.guideTitle")}
		/>
	);
}

const DeviceSetupStep = forwardRef<HTMLDivElement, { children: React.ReactNode; substeps: DeviceSetupKey[] }>(
	function DeviceSetupStep({ children, substeps }, ref) {
		const deviceLabels: string[] = [];

		if (substeps.includes("videoRecordingMode")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.camera"));
		}
		if (substeps.includes("audioRecordingMode")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.microphone"));
		}
		if (substeps.includes("recordScreen")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.screen"));
		}

		// Fallback in case substeps is empty
		if (deviceLabels.length === 0) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.camera"));
		}

		const lf = new Intl.ListFormat(i18nPackage.language, { style: "long", type: "conjunction" });
		return (
			<BasicStep
				description={i18nPackage.t("beforeQuiz.deviceSetup.description", { devices: lf.format(deviceLabels) })}
				ref={ref}
			>
				{children}
			</BasicStep>
		);
	},
);

export default DeviceSetupStep;
